import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { SearchOption } from '../../../models/types/components/Search.type';

@Component({
    selector: 'app-search-input',
    imports: [],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchInputComponent), multi: true }
    ],
    templateUrl: './search-input.component.html',
    styleUrl: './search-input.component.css'
})
export class SearchInputComponent implements ControlValueAccessor {

  // VIEW CHILDS
  @ViewChild('inputContainer')
  public inputContainer!: ElementRef<HTMLDivElement>;       // Contenedor del input

  @ViewChild('inputField')
  public inputField!:     ElementRef<HTMLInputElement>;     // Campo de texto del input


  // INPUTS
  @Input({required: true})
  public options:         SearchOption[]  = [];             // Opciones de búsqueda
  
  @Input()
  public placeholder:     string          = '';             // Placeholder del input

  @Input()
  public disabled:        boolean         = false;          // Estado de deshabilitado

  @Input()
  public type:            'text'|'number' = 'text';         // Tipo de input

  @Input()
  public minChars:        number          = 1;              // Número mínimo de caracteres para filtrar

  @Input()
  public cssClasses:      string[]        = [];             // Clases CSS

  // OUTPUTS
  @Output()
  public filterValues:    EventEmitter<SearchOption[]>    = new EventEmitter<SearchOption[]>();     // Emite las opciones filtradas

  @Output()
  public updatedValue:    EventEmitter<SearchOption|null> = new EventEmitter<SearchOption|null>();  // Emite la opción seleccionada 

  @Output()
  public hoveredValue:    EventEmitter<SearchOption>      = new EventEmitter<SearchOption>();       // Emite la opción resaltada

  
  // HOST LISTENER
  @HostListener('document:click', ['$event'])
  // Método que escucha los clicks fuera del input
  public clickOutside(event: MouseEvent): void {
    if(!this.inputContainer.nativeElement.contains(event.target as Node)) {
      this.filteredOptions = [];
      this.onTouched();
    }
  }

  // STATE
  protected value:            SearchOption | null = null; // Valor seleccionado

  protected inputValue:       string              = '';   // Valor del input (texto que se muestra)

  protected filteredOptions:  SearchOption[]      = [];   // Opciones filtradas

  protected highlightedIndex: number              = -1;   // Índice de la opción resaltada

  // GETTERS
  protected get openResults(): boolean {                  // Determina si se muestran los resultados
    return this.filteredOptions.length > 0 && this.value === null;
  }

  protected get cssClass(): string {                      // Clases CSS del input
    return ['input',...this.cssClasses].join(' ');
  }

  // CONTROL VALUE ACCESSOR METHODS
  private onChange: (val: SearchOption)=> SearchOption = (val: SearchOption): SearchOption => {return val};

  private onTouched: ()=> void = (): void => {return};
 
  public registerOnChange(fn: (val: SearchOption)=> SearchOption ):  void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: ()=>void ): void {
    this.onTouched = fn;
  }

  public writeValue(value: SearchOption): void {
    this.value = value;
  }

  // METHODS

  /**
   * Método que se ejecuta al cambiar el valor del input.
   * Se da valor a inputValue y se comprueba si ya existe un valor seleccionado, si es asi se limpia el valor y se
   * vuelve a asignar el valor del input para que el filtrado empiece de nuevo.
   * Si no, se filtra directamente y se resetea el índice de la opción resaltada.
   * @param event 
   */
  public onInputChange(event: Event): void {

    // Se obtiene el valor del input y se adjunta a inputValue
    const inputValue: string = (event.target as HTMLInputElement).value;
    this.inputValue = inputValue;
    
    // Si el valor del input no es nulo y el valor del input no es igual al valor seleccionado, se limpia el valor
    if(this.value !== null && this.inputValue !== this.value.label){
      this.resetInput();
      this.inputValue = inputValue;
    }else{
      this.onChange(this.value!);
    }
    
    this.filterOptions(this.inputValue);

    this.highlightedIndex = -1;
    
  }

  /**
   * Método que selecciona una opción.
   * Se asigna la opción seleccionada al valor del input, se llama a los métodos de control de valor y de control de estado,
   * se limpian las opciones filtradas y se emite la opción seleccionada.
   * @param option 
   */
  public selectOption(option: SearchOption): void {
    
    // Asignamos la opción seleccionada al valor del input
    this.value      = option;
    this.inputValue = option.label;
    
    // Llamamos a los métodos de control de valor y de control de estado
    this.onChange(this.value);
    this.onTouched();
    
    // Limpiamos las opciones filtradas
    this.filteredOptions = [];
    
    // Emitimos la opción seleccionada
    this.updatedValue.emit(option);
  
  }

  /**
   * Método que resalta una opción.
   * Se emite la opción resaltada.
   * @param option 
   */
  public hoverOption(option: SearchOption): void {
    this.hoveredValue.emit(option);
  }

  /**
   * Método que se ejecuta al pulsar una tecla encima del input.
   * Otorga control con el teclado sobre los resultados del input, para ello, analiza si se pulsan estas 4 teclas:
   * 1. Flecha abajo: Resalta la siguiente opción. Suma 1 al índice de la opción resaltada.
   * 2. Flecha arriba: Resalta la opción anterior. Resta 1 al índice de la opción resaltada.
   * 3. Enter: Selecciona la opción resaltada. Se llama al método selectOption.
   * 4. Escape: Limpia el input. Se llama al método resetInput.
   * 
   * Se tiene que repetir el event.preventDefault() dentro del switch porque si no impediria el uso de ninguna tecla que no sea las 4 mencionadas.
   * @param event 
   * @returns 
   */
  public onKeyDown(event: KeyboardEvent): void {
    
    // Si no hay resultados abiertos, no se hace nada
    if(!this.openResults) return;

    
    switch (event.key) {
      
      // Si se pulsa hacia abajo, se suma 1 al índice de la opción resaltada y se comprueba que no sea mayor que el número de opciones filtradas
      case 'ArrowDown':
        
        event.preventDefault();
        
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        
        if (this.highlightedIndex === -1) this.highlightedIndex = 0;

        this.hoverOption(this.filteredOptions[this.highlightedIndex]);
        
      break;
      
      // Si se pulsa hacia arriba, se resta 1 al índice de la opción resaltada y se comprueba que no sea menor que -1
      case 'ArrowUp':
        
        event.preventDefault();
        
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);

        this.hoverOption(this.filteredOptions[this.highlightedIndex]);


      break;
      
      // Si se pulsa Enter, se selecciona la opción resaltada
      case 'Enter':
        
        event.preventDefault();

        if (this.highlightedIndex !== -1) this.selectOption(this.filteredOptions[this.highlightedIndex]);
        
      break;
      
      // Si se pulsa Escape, se limpia el input y se emite la opción resaltada como undefined
      case 'Escape':
        
        event.preventDefault();

        this.resetInput();
        this.hoveredValue.emit(undefined);
        
      break;
    }
    

  }

  /**
   * Método que se ejecuta al enfocar el input.
   * Si el valor no es nulo, se limpia el valor.
   * Se filtran las opciones con el valor del input.
   */
  public onFocus(): void {
    if(this.value !== null) this.value = null;

    this.filterOptions(this.inputValue);
  }

  /**
   * Método que filtra las opciones con el texto del input y emite las opciones filtradas.
   * Si el texto es nulo o tiene menos caracteres que el mínimo, se limpian las opciones filtradas.
   * Si no, se normaliza el texto a minúsculas y se filtran las opciones que contienen el texto y posteriormente se emiten.
   * @param text 
   * @returns 
   */
  private filterOptions(text: string): void {
    
    // Si el texto es nulo o tiene menos caracteres que el mínimo, se limpian las opciones filtradas y se emiten
    if(!text || text.length < this.minChars) {
      this.filteredOptions = [];
      this.filterValues.emit(this.filteredOptions);
      return;
    }

    // Normalizamos el texto a minúsculas
    const lowerText = text.toLowerCase();

    // Filtramos las opciones que contienen el texto normal
    this.filteredOptions = this.options.filter((option: SearchOption) => option.label.toLowerCase().includes(lowerText));

    // Emitimos las opciones filtradas
    this.filterValues.emit(this.filteredOptions);

  }

  /**
   * Método que limpia el input.
   * Se limpia el valor, el texto del input, las opciones filtradas y el índice de la opción resaltada.
   * Se llama a los métodos de control de valor y de control de estado y se emite el valor.
   */
  public resetInput(): void {

    this.value = null;
    this.inputValue = '';
    this.filteredOptions = [];
    this.highlightedIndex = -1;
    this.onTouched();
    this.onChange(this.value!);
    this.updatedValue.emit(this.value); 

  }
  
}
